// src/components/admin/ImageCropModal.jsx
//
// Lets an admin frame an uploaded service/product image before it is saved.
// The frame has the same proportions as the Customer Portal card image (4:3 for
// services, square for products), so what is framed here is exactly what
// customers see. Drag to reposition, use the slider to zoom, or use the two
// buttons to fill the frame (crops) or fit the whole image (adds white space).
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import '../../styles/imagecrop.css';

// Output canvases — must match the card image boxes in the CSS
export const IMAGE_PRESETS = {
  service: { width: 800, height: 600, label: 'service' },
  product: { width: 1000, height: 1000, label: 'product' }
};

const MAX_ZOOM_OVER_FILL = 4;

function ImageCropModal({ file, kind, onCancel, onConfirm }) {
  const preset = IMAGE_PRESETS[kind] || IMAGE_PRESETS.service;
  const aspect = preset.width / preset.height;

  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const [src, setSrc] = useState('');
  const [natural, setNatural] = useState(null);   // { w, h }
  const [frameW, setFrameW] = useState(0);
  const [scale, setScale] = useState(1);
  const [center, setCenter] = useState({ x: 0, y: 0 }); // image centre relative to frame centre, px
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const frameH = frameW / aspect;

  // Object URL for the chosen file
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Track the rendered frame width so the maths follows responsive sizing
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const measure = () => setFrameW(el.clientWidth);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const scaleLimits = useCallback(() => {
    if (!natural || !frameW) return { fit: 1, fill: 1, max: 1 };
    const fit = Math.min(frameW / natural.w, frameH / natural.h);
    const fill = Math.max(frameW / natural.w, frameH / natural.h);
    return { fit, fill, max: fill * MAX_ZOOM_OVER_FILL };
  }, [natural, frameW, frameH]);

  // Keep the image from being dragged so far that a gap opens up inside the frame
  const clampCenter = useCallback((c, s) => {
    if (!natural) return c;
    const dispW = natural.w * s;
    const dispH = natural.h * s;
    const maxX = Math.max(0, (dispW - frameW) / 2);
    const maxY = Math.max(0, (dispH - frameH) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, c.x)),
      y: Math.min(maxY, Math.max(-maxY, c.y))
    };
  }, [natural, frameW, frameH]);

  const fillFrame = useCallback(() => {
    const { fill } = scaleLimits();
    setScale(fill);
    setCenter({ x: 0, y: 0 });
  }, [scaleLimits]);

  const fitWhole = useCallback(() => {
    const { fit } = scaleLimits();
    setScale(fit);
    setCenter({ x: 0, y: 0 });
  }, [scaleLimits]);

  // Start filled once both the image and the frame size are known
  const initialisedRef = useRef(false);
  useEffect(() => {
    if (natural && frameW && !initialisedRef.current) {
      initialisedRef.current = true;
      fillFrame();
    }
  }, [natural, frameW, fillFrame]);

  // If the frame is resized (e.g. rotating a phone) keep the image legal
  useEffect(() => {
    if (!initialisedRef.current) return;
    const { fit, max } = scaleLimits();
    const s = Math.min(max, Math.max(fit, scale));
    if (s !== scale) setScale(s);
    setCenter(prev => clampCenter(prev, s));
  }, [frameW]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !working) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, working]);

  const onPointerDown = (e) => {
    if (!natural) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: center };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setCenter(clampCenter({
      x: d.origin.x + (e.clientX - d.startX),
      y: d.origin.y + (e.clientY - d.startY)
    }, scale));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onSlider = (e) => {
    const s = Number(e.target.value);
    setScale(s);
    setCenter(prev => clampCenter(prev, s));
  };

  const nudge = (dx, dy) => setCenter(prev => clampCenter({ x: prev.x + dx, y: prev.y + dy }, scale));
  const onFrameKeyDown = (e) => {
    const step = 10;
    if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-step, 0); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); nudge(step, 0); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); nudge(0, -step); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); nudge(0, step); }
  };

  const handleConfirm = async () => {
    if (!natural || !imgRef.current) return;
    setWorking(true);
    setError('');
    try {
      const k = preset.width / frameW; // frame px -> output px
      const dispW = natural.w * scale;
      const dispH = natural.h * scale;
      const left = frameW / 2 + center.x - dispW / 2;
      const top = frameH / 2 + center.y - dispH / 2;

      const canvas = document.createElement('canvas');
      canvas.width = preset.width;
      canvas.height = preset.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(imgRef.current, left * k, top * k, dispW * k, dispH * k);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.88));
      if (!blob) throw new Error('Could not create image');
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
      onConfirm(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }));
    } catch (err) {
      console.error('Image crop error:', err);
      setError('Sorry, this image could not be processed. Please try a different file.');
      setWorking(false);
    }
  };

  const { fit, max } = scaleLimits();
  const dispW = natural ? natural.w * scale : 0;
  const dispH = natural ? natural.h * scale : 0;

  return (
    <div className="image-crop-overlay" role="dialog" aria-modal="true" aria-label="Position image">
      <div className="image-crop-modal">
        <h3 className="image-crop-title">Position your {preset.label} image</h3>
        <p className="image-crop-help">
          This is exactly how customers will see it. Drag to move the image and use the slider to zoom.
          Keep the main item (for example the whole bottle, or the face) inside the frame.
        </p>

        <div
          className="image-crop-frame"
          ref={frameRef}
          style={{ aspectRatio: `${preset.width} / ${preset.height}` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onFrameKeyDown}
          tabIndex={0}
          aria-label="Image position. Drag, or use the arrow keys, to move the image."
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              onError={() => setError('This file could not be read as an image.')}
              style={natural && frameW ? {
                width: dispW,
                height: dispH,
                left: frameW / 2 + center.x - dispW / 2,
                top: frameH / 2 + center.y - dispH / 2
              } : { visibility: 'hidden' }}
            />
          )}
        </div>

        <div className="image-crop-controls">
          <label className="image-crop-zoom">
            <span>Zoom</span>
            <input
              type="range"
              min={fit}
              max={max}
              step={(max - fit) / 200 || 0.001}
              value={scale}
              onChange={onSlider}
              disabled={!natural}
            />
          </label>
          <div className="image-crop-presets">
            <button type="button" className="image-crop-secondary" onClick={fillFrame} disabled={!natural}>
              Fill frame
            </button>
            <button type="button" className="image-crop-secondary" onClick={fitWhole} disabled={!natural}>
              Fit whole image
            </button>
          </div>
        </div>

        {error && <p className="image-crop-error">{error}</p>}

        <div className="image-crop-actions">
          <button type="button" className="cancel-button" onClick={onCancel} disabled={working}>
            Cancel
          </button>
          <button type="button" className="save-button" onClick={handleConfirm} disabled={!natural || working}>
            {working ? 'Processing...' : 'Use this image'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImageCropModal;
